// src/utils/handle-chunk-errors.ts
/**
 * Handle chunk loading errors that occur when users have cached old chunks
 * after a new deployment. This auto-reloads the page once to fetch new chunks.
 */

const RELOAD_KEY = "chunk-error-reload-attempted";

export function setupChunkErrorHandler() {
  // Listen for errors during dynamic imports
  window.addEventListener("error", (event) => {
    const target = event.target as HTMLScriptElement | HTMLLinkElement | null;

    // Check if this is a chunk loading error
    const isChunkError =
      event.message?.includes("Failed to fetch dynamically imported module") ||
      event.message?.includes("Importing a module script failed") ||
      (target?.tagName === "SCRIPT" && target?.src) ||
      (target?.tagName === "LINK" && target?.href);

    if (isChunkError) {
      const hasReloaded = sessionStorage.getItem(RELOAD_KEY);

      if (!hasReloaded) {
        console.log("Chunk loading failed - reloading page to fetch new version");

        // Mark that we've attempted reload (prevents infinite reload loop)
        sessionStorage.setItem(RELOAD_KEY, "true");

        // Reload the page to get fresh chunks
        window.location.reload();
      } else {
        // If we've already reloaded and still getting errors, clear the flag
        // and show a helpful error to the user
        sessionStorage.removeItem(RELOAD_KEY);
        console.error(
          "Chunk loading failed after reload. Try clearing your browser cache (Cmd+Shift+R on Mac, Ctrl+Shift+F5 on Windows)",
        );
      }
    }
  });

  // Also handle unhandled promise rejections (for dynamic imports that fail)
  window.addEventListener("unhandledrejection", (event) => {
    const error = event.reason;

    if (
      error?.message?.includes("Failed to fetch dynamically imported module") ||
      error?.message?.includes("Importing a module script failed")
    ) {
      const hasReloaded = sessionStorage.getItem(RELOAD_KEY);

      if (!hasReloaded) {
        console.log("Chunk loading failed - reloading page to fetch new version");
        sessionStorage.setItem(RELOAD_KEY, "true");
        window.location.reload();

        // Prevent the error from showing in console
        event.preventDefault();
      }
    }
  });

  // Clear the reload flag on successful page load
  window.addEventListener("load", () => {
    // Small delay to ensure everything loaded successfully
    setTimeout(() => {
      sessionStorage.removeItem(RELOAD_KEY);
    }, 1000);
  });
}
